<script setup lang="ts">
// Shows useConfirm's question as a modal dialog: Esc, Cancel or a click outside says no; the action's own button
// says yes. A removal focuses Cancel first, anything else its action. Focus goes back where it was afterwards.
import { nextTick, ref, watch } from "vue";
import Icon from "@/components/ui/Icon.vue";
import { useConfirm } from "@/composables/useConfirm";

const { pending, answer } = useConfirm();
const dialog = ref<HTMLDialogElement | null>(null);
let before: HTMLElement | null = null;

watch(pending, async (p) => {
  const d = dialog.value;
  if (!d) return;
  if (p) {
    if (!d.open) {
      before = document.activeElement as HTMLElement | null;
      if (typeof d.showModal === "function") d.showModal(); else d.setAttribute("open", "");
    }
    await nextTick();
    d.querySelector<HTMLElement>("[data-autofocus]")?.focus();
  } else if (d.open) {
    if (typeof d.close === "function") d.close(); else d.removeAttribute("open");
    before?.focus?.();
    before = null;
  }
});

// a click on the backdrop lands on the dialog itself; inside, on the panel
function outside(e: MouseEvent) {
  if (e.target === dialog.value) answer(false);
}
</script>

<template>
  <dialog ref="dialog" class="confirm" data-test="confirm-dialog" role="alertdialog" aria-labelledby="confirm-title"
          :aria-describedby="pending?.body ? 'confirm-body' : undefined" @cancel.prevent="answer(false)" @click="outside">
    <div v-if="pending" class="panel" :data-danger="pending.danger ? '' : undefined">
      <span class="mark" aria-hidden="true"><Icon :name="pending.danger ? 'warning' : 'info'" :size="18" /></span>
      <div class="words">
        <h2 id="confirm-title">{{ pending.title }}</h2>
        <p v-if="pending.body" id="confirm-body">{{ pending.body }}</p>
      </div>
      <div class="actions">
        <button type="button" class="btn" data-test="confirm-no" :data-autofocus="pending.danger ? '' : undefined"
                @click="answer(false)">{{ pending.cancel ?? "Cancel" }}</button>
        <button type="button" class="btn yes" :class="{ 'btn--primary': !pending.danger }" data-test="confirm-yes"
                :data-autofocus="pending.danger ? undefined : ''" @click="answer(true)">{{ pending.confirm }}</button>
      </div>
    </div>
  </dialog>
</template>

<style scoped>
.confirm {
  width: min(calc(100vw - 32px), 460px); margin: auto; padding: 0; color: var(--ink); background: var(--bg-elev);
  border: 1px solid var(--line); border-radius: var(--radius-m); box-shadow: var(--shadow-3); overflow: visible;
}
.confirm[open] { animation: rise var(--speed-med) var(--ease-out); }
.confirm::backdrop { background: rgb(16 17 20 / 45%); backdrop-filter: blur(2px); }
.panel { display: grid; grid-template-columns: auto 1fr; gap: var(--space-4) var(--space-5); padding: var(--space-6); }
.mark {
  display: grid; place-items: center; width: 36px; height: 36px; border-radius: 50%;
  color: var(--brand); background: var(--brand-soft);
}
.panel[data-danger] .mark { color: var(--failed); background: var(--failed-soft); }
.words { display: grid; gap: var(--space-3); min-width: 0; padding-top: 6px; }
h2 { font-size: var(--text-md); font-weight: 600; line-height: 1.35; overflow-wrap: anywhere; }
p { color: var(--muted); font-size: var(--text-sm); line-height: 1.55; overflow-wrap: anywhere; }
.actions { grid-column: 1 / -1; display: flex; justify-content: flex-end; flex-wrap: wrap; gap: var(--space-3); margin-top: var(--space-3); }
.panel[data-danger] .yes { background: var(--failed); border-color: var(--failed); color: var(--bg); font-weight: 600; }
.panel[data-danger] .yes:hover { filter: brightness(1.08); }
@keyframes rise { from { opacity: 0; transform: translateY(8px) scale(.98); } }
@media (prefers-reduced-motion: reduce) { .confirm[open] { animation: none; } }
@media (max-width: 480px) {
  .panel { grid-template-columns: 1fr; padding: var(--space-5); }
  .actions { flex-direction: column-reverse; }
  .actions .btn { width: 100%; }
}
</style>
